import { m } from '@aimfora/i18n'
import { FormField, focusFirstInvalid, Notice, Textarea } from '@aimfora/ui'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { FormError, SubmitButton, TextField } from '~/components/form-parts'
import { requestBetaAccessFn } from '~/functions/beta.functions'
import { errorMessage } from '~/lib/domain-messages'
import { betaRequestSchema } from '~/lib/schemas'

export const Route = createFileRoute('/beta')({
  head: () => ({ meta: [{ title: `${m.beta_title()} - ${m.app_name()}` }] }),
  component: BetaPage,
})

/** The public form for asking for beta access. An admin approves the request and invites. */
function BetaPage() {
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [trap, setTrap] = useState('')

  const form = useForm({
    defaultValues: { name: '', email: '', school: '', role: '', message: '' },
    validators: { onBlur: betaRequestSchema(), onSubmit: betaRequestSchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setServerError(null)
      try {
        await requestBetaAccessFn({ data: { ...value, website: trap } })
        setSentTo(value.email.trim())
      } catch (error) {
        setServerError(errorMessage(error))
      }
    },
  })

  if (sentTo) {
    return (
      <AuthCard
        title={m.beta_done_title()}
        footer={
          <Link className={textLinkClass} to="/">
            {m.beta_back_home()}
          </Link>
        }
      >
        <Notice tone="ok" role="status">
          {m.beta_done_body({ email: sentTo })}
        </Notice>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title={m.beta_title()}
      footer={
        <>
          {m.beta_have_invitation()}{' '}
          <Link className={textLinkClass} to="/signup">
            {m.beta_have_invitation_link()}
          </Link>
        </>
      }
    >
      <form
        ref={formRef}
        noValidate
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <p className="-mt-1 text-ink-soft text-sm">{m.beta_intro()}</p>
        <FormError message={serverError} />
        <form.Field name="name">
          {(field) => (
            <TextField field={field} label={m.beta_name_label()} autoComplete="name" autoFocus />
          )}
        </form.Field>
        <form.Field name="email">
          {(field) => (
            <TextField
              field={field}
              label={m.beta_email_label()}
              type="email"
              autoComplete="email"
            />
          )}
        </form.Field>
        <form.Field name="school">
          {(field) => (
            <TextField
              field={field}
              label={m.beta_school_label()}
              required={false}
              autoComplete="organization"
            />
          )}
        </form.Field>
        <form.Field name="role">
          {(field) => (
            <TextField
              field={field}
              label={m.beta_role_label()}
              hint={m.beta_role_hint()}
              required={false}
              autoComplete="organization-title"
            />
          )}
        </form.Field>
        <form.Field name="message">
          {(field) => (
            <FormField label={m.beta_message_label()}>
              {(control) => (
                <Textarea
                  {...control}
                  name={field.name}
                  rows={3}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
              )}
            </FormField>
          )}
        </form.Field>
        {/* Out of sight and out of the tab order; only a robot fills it in */}
        <div aria-hidden="true" className="absolute -left-[9999px]">
          <label>
            {m.beta_trap_label()}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={trap}
              onChange={(event) => setTrap(event.target.value)}
            />
          </label>
        </div>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(pending) => (
            <SubmitButton pending={pending} className="w-full">
              {m.beta_submit()}
            </SubmitButton>
          )}
        </form.Subscribe>
      </form>
    </AuthCard>
  )
}
